<template lang="pug">
    div
        v-snackbar(
            v-model="snackbar.show" 
            :color="snackbar.color" 
            :timeout="snackbar.timeout" 
            top 
            width="200" 
            height="40" 
            min-width="0" 
            min-height="0"
        )
            template(v-if="!mini") 
                | {{ snackbar.text }}
            template(v-else)
                v-icon(small color="white") {{snackbar.icon}}
        v-menu(
            v-if="validPinnedAccounts"
            offset-x
            min-width="200"
            right
        )
            template(v-slot:activator="{ on: menu, attrs: menuAttrs }")
                v-tooltip(right)
                    template(v-slot:activator="{ on: tooltip, attrs: tooltipAttrs }")
                        v-btn(
                            icon
                            :loading="accountSwitchLoading"
                            v-bind="{ ...menuAttrs, ...tooltipAttrs }"
                            v-on="{ ...menu, ...tooltip }"
                        )
                            v-icon(small color="white") mdi-account-switch
                    | Switch to a pinned account

            v-list(dense)
                v-subheader(style="border-bottom: 1px solid var(--off-white);")
                    v-icon.ml-1(small) mdi-pin-outline
                    span.ml-2(style="font-size: 14px;") Pinned Accounts
                v-list-item(
                    v-for="pinnedAccount in pinnedAccounts" 
                    :key="pinnedAccount.account_id" 
                    @click="updateUser(pinnedAccount.account_id)" 
                    :disabled="isActivatedAccount(pinnedAccount.account_id)"
                )
                    v-list-item-content
                        v-list-item-title {{ pinnedAccount.name }}
                            v-icon(color="var(--success)" v-if="isActivatedAccount(pinnedAccount.account_id)")
                                | mdi-circle-medium


</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from '@/store';

// Data
import { UpdateUser } from '@/data';
import { clearFeatureFlagCache } from '@/data/feature_flag_check';

// Helpers
import { getCookie, setCookie } from '@/helpers/cookie';

export default Vue.extend({
    data() {
        return {
            accountSwitchLoading: false,
            pinnedAccountsMenuOpen: false,
            pinnedAccounts: [],
            snackbar: {
                show: false,
                timeout: 1000,
                color: '',
                text: '',
                icon: '',
            },
        }
    },
    methods: {
        getPinnedAccounts() {
            try {
                const cookieValue = getCookie('v_accounts_cookies_pinned_accounts');
                this.pinnedAccounts = cookieValue ? JSON.parse(cookieValue) : [];
            } catch {
                this.pinnedAccounts = [];
            }
        },
        togglePinnedAccountsMenu() {
            this.pinnedAccountsMenuOpen = !this.pinnedAccountsMenuOpen;
        },
        updateRecentAccountsCookie(account: any) {
            if (!account || !account.name || !account.account_id) {
                return;
            }

            let currentAccounts: any[] = [];
            try {
                const recentCookieValue = getCookie('v_accounts_cookies_recent_accounts');
                currentAccounts = recentCookieValue ? JSON.parse(recentCookieValue) : [];
            } catch {
                currentAccounts = [];
            }

            // remove the account if it's already in the array
            const existsIndex = currentAccounts.findIndex((a: any) => a.account_id === account.account_id);
            if (existsIndex > -1) {
                currentAccounts.splice(existsIndex, 1);
            }
            // add to beginning of array and set cookie
            currentAccounts.unshift(account);
            setCookie('v_accounts_cookies_recent_accounts', JSON.stringify(currentAccounts));
            if (this.$route.name !== 'account_switcher_index') {
                this.$router.go(0);
            }
        },
        updateUser(accountId: number) {
            const user_id = this.currentUser.user_id;
            if (!user_id) {
                return;
            }

            this.accountSwitchLoading = true;

            const options = {
                activated_account_id: accountId,
            };

            UpdateUser(user_id, options).then((response) => {
                if (!response.Error && response.Data?.user?.user_id > 0) {
                    if (response.Data?.user.account_id === accountId) {
                        this.showSnackbar('success', 'Updated user.', 'mdi-check');
                        clearFeatureFlagCache();
                        VolieState.reloadUser();
                    }
                } else {
                    this.showSnackbar('error', 'Could not update user.', 'mdi-close');
                }
            }).catch((error) => {
                console.error('Error updating user:', error);
            }).finally(() => {
                this.accountSwitchLoading = false;
            });
        },
        isActivatedAccount(accountId: number): boolean {
            return this.currentUser.activated_account_id === accountId;
        },
        showSnackbar(color: string, text: string, icon?: string) {
            this.snackbar.color = color;
            this.snackbar.text = text;
            this.snackbar.show = true;
            this.snackbar.icon = '';
            if (icon) {
                this.snackbar.icon = icon;
            }
        },
    },
    computed: {
        currentUser() {
            return VolieState.currentUser();
        },
        validPinnedAccounts() {
            return this.pinnedAccounts.length > 0;
        },
    },
    props: {
        mini: {
            type: Boolean,
            default: false,
        },
    },
    mounted() {
        this.getPinnedAccounts();
    },
    watch: {
        'currentUser.account': {
            handler(newVal) {
                if (!newVal || !newVal.name || !newVal.account_id) {
                    return;
                }
                this.updateRecentAccountsCookie(newVal);
            },
        },
    },
})
</script>

<style lang="scss" scoped>
    .list-item-content {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
    }

    ::v-deep .v-list-item__title {
        font-size: 14px !important;
        font-weight: 400 !important;
    }
</style>